import React, { useEffect } from 'react';
import { IoClose } from 'react-icons/io5';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, Trash2, X } from 'lucide-react';

const CartSidebar = ({ isOpen, onClose }) => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  const subtotal = cartItems.reduce((total, item) => {
    // If item has quantity break, use the tier total price directly
    if (item.quantityBreak?.tierTotalPrice) {
      return total + item.quantityBreak.tierTotalPrice;
    }
    // Otherwise, use regular price calculation
    const linePrice = (item.price ?? item.originalPrice ?? 0) * item.quantity;
    return total + linePrice;
  }, 0);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  // Prevent background scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none'; // Disable touch scrolling
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <>
      {/* Backdrop with better mobile interaction */}
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Cart Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 transform transition-all duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"} flex flex-col`} // 🟢 Added flex-col
      >

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ShoppingCart className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">Shopping Cart</h2>
              <p className="text-sm text-gray-500">
                {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 group"
            aria-label="Close cart"
          >
            <X className="h-5 w-5 text-gray-500 group-hover:text-gray-700" />
          </button>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto" style={{ height: "calc(100vh - 200px)" }}>
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="p-4 bg-gray-100 rounded-full mb-6">
                <ShoppingCart className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-6">Add some products to get started</p>
              <button
                onClick={() => {
                  onClose()
                  navigate("/")

                }}
                className="bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {Array.isArray(cartItems) && cartItems.map((item) => {
                const isBogoFree = item.offerType === 'BOGO' && item.isFreeItem;

                return (
                <div
                  key={`${item.bundleKey || item.productId}-${item.isFreeItem ? 'free' : 'main'}`}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={`${item.imageUrl}?tr=w-80,q-70,f-webp` || "/placeholder.svg"}
                        alt={item.title}
                        width="80"
                        height="80"
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                        style={{ aspectRatio: "1/1" }}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 line-clamp-2">
                          {item.title}
                        </h4>
                        {item.offerType === 'BOGO' && (
                          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${isBogoFree ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>
                            {item.offerLabel || (isBogoFree ? 'Free Product' : 'Main Product')}
                          </span>
                        )}
                      </div>
                      {item.comboLabel && (
                        <p className="text-xs text-gray-500 mb-1">
                          Combo: {item.comboLabel}
                        </p>
                      )}
                      {item.variantLabel && (
                        <p className="text-xs text-gray-500 mb-1">
                          Variant: {item.variantLabel}
                        </p>
                      )}
                      {item.offerMeta?.primaryColor && !isBogoFree && (
                        <p className="text-xs text-gray-500">
                          Color: {item.offerMeta.primaryColor}
                        </p>
                      )}
                      {item.offerMeta?.freeColor && isBogoFree && (
                        <p className="text-xs text-gray-500">
                          Color: {item.offerMeta.freeColor}
                        </p>
                      )}

                      {/* Price */}
                      <div className="flex text-base items-center gap-2 mb-3 price-tag">
                      {item.originalPrice && item.originalPrice > item.price && (
                          <span className=" text-gray-500 line-through">
                            Rs. {item.originalPrice}
                          </span>
                        )}
                        <span className={`text-[#ec0101] ${isBogoFree ? 'line-through text-gray-400' : ''}`}>
                          Rs. {item.quantityBreak?.tierTotalPrice 
                            ? Math.round(item.quantityBreak.tierTotalPrice).toLocaleString()
                            : (item.price * item.quantity).toLocaleString()}
                        </span>
                        {isBogoFree && <span className="text-xs text-green-600">(Free)</span>}
                      </div>

                      {/* Quantity Controls and Remove */}
                      <div className="flex items-center justify-between gap-2">
                        {isBogoFree ? (
                          <div className="text-xs text-gray-500">
                            Quantity matches main item
                          </div>
                        ) : (
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <button
                              onClick={() => updateQuantity(item, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="p-2 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-4 w-4 text-gray-600" />
                          </button>
                          <span className="px-4 py-2 text-sm font-medium text-gray-900 min-w-[3rem] text-center">
                            {item.quantity}
                          </span>
                          <button
                              onClick={() => updateQuantity(item, item.quantity + 1)}
                            disabled={item.quantity >= item.stock}
                            className="p-2 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                        )}

                        {isBogoFree ? (
                          <div className="text-xs text-gray-400">Remove main item to remove offer</div>
                        ) : (
                        <button
                            onClick={() => removeFromCart(item)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200 group"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4 group-hover:text-red-600" />
                        </button>
                        )}
                      </div>

                      {/* Stock Warning */}
                      {item.quantity >= item.stock && !isBogoFree && (
                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                          <span className="w-1 h-1 bg-amber-600 rounded-full"></span>
                          Maximum stock reached
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-200 bg-white p-6">
            {/* Subtotal */}
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600 text-xl ">Subtotal</span>
              <span className="text-xl font-medium text-gray-900">Rs. {subtotal.toLocaleString()}</span>
            </div>

            {/* Shipping Info */}
            <div className="text-xs text-gray-500 mb-4 text-center">Free Home Delivery</div>

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              className="w-full bg-primary hover:bg-primary-700 text-white py-3  rounded-lg  flex items-center justify-center gap-3"
            >
              <ShoppingCart className="h-5 w-5" />
               Checkout
            </button>

          </div>
        )}
      </div>
    </>
  );
};

export default CartSidebar;